import { useState } from 'react'
import type { PuzzleRendererProps } from '@/game/PuzzleRenderer'
import { useServerClock } from '@/game/useServerClock'

type Config = { instructions?: string; minSeconds?: number; maxSeconds?: number; requireStart?: boolean }
type State = { requireStart?: boolean; startedAt?: string | null; canFinishAt?: string | null; minSeconds?: number | null; maxSeconds?: number | null }

/** Self-certified physical task with optional server-recorded timing. Submits { done: true }. */
export function ActionPuzzle({ puzzle, onSubmit, onAction, disabled, serverTime }: PuzzleRendererProps) {
  const config = puzzle.publicConfig as Config
  const state = puzzle.state as State
  const now = useServerClock(serverTime)
  const [busy, setBusy] = useState(false)

  const startedAt = state.startedAt ? Date.parse(state.startedAt) : null
  const canFinishAt = state.canFinishAt ? Date.parse(state.canFinishAt) : null
  const elapsed = startedAt ? Math.max(0, Math.floor((now - startedAt) / 1000)) : 0
  const waitLeft = canFinishAt ? Math.max(0, Math.ceil((canFinishAt - now) / 1000)) : 0
  const needsStart = (state.requireStart ?? config.requireStart ?? false) && !startedAt

  async function run(fn: () => void | Promise<void>) {
    setBusy(true)
    try {
      await fn()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="puzzle-form">
      {config.instructions && <p>{config.instructions}</p>}
      {needsStart ? (
        <button type="button" className="primary" disabled={disabled || busy} onClick={() => void run(() => onAction('start'))}>
          {busy ? 'Starting…' : 'Start'}
        </button>
      ) : (
        <>
          {startedAt && (
            <p className="timer" role="timer">
              {formatSeconds(elapsed)}
              {state.maxSeconds ? <span className="muted"> / {formatSeconds(state.maxSeconds)}</span> : null}
            </p>
          )}
          {waitLeft > 0 && (
            <p className="muted" role="status">
              You can finish in {waitLeft}s.
            </p>
          )}
          <button
            type="button"
            className="primary"
            disabled={disabled || busy || waitLeft > 0}
            onClick={() => void run(() => onSubmit({ done: true }))}
          >
            {busy || disabled ? 'Saving…' : 'Done'}
          </button>
        </>
      )}
    </div>
  )
}

function formatSeconds(total: number) {
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}
